/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, Suspense } from 'react';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import VideoExperienceHub from './components/VideoExperienceHub';
import FullEcosystemHub from './components/FullEcosystemHub';
import { WorkspaceCloudSqlHub } from './components/WorkspaceCloudSqlHub';
import FourDomainNavigator from './components/FourDomainNavigator';
import UniversalStartDoor from './components/UniversalStartDoor';
import InteractiveAppShowcase from './components/InteractiveAppShowcase';
import StartingPointDashboard from './components/StartingPointDashboard';
import QuranSeerahJourneys from './components/QuranSeerahJourneys';
import PhotoAlbumGallery from './components/PhotoAlbumGallery';
import KnowledgeHub from './components/KnowledgeHub';
import PillarsGrid from './components/PillarsGrid';
import WellnessCheck from './components/WellnessCheck';
import IdeaLabGate from './components/IdeaLabGate';
import GoogleClassroomHub from './components/GoogleClassroomHub';
import ProjectTechOverview from './components/ProjectTechOverview';
import FounderStory from './components/FounderStory';
import HumanJourneyKernel from './components/HumanJourneyKernel';
import UniversalMeYooOS from './components/UniversalMeYooOS';
import Roadmap from './components/Roadmap';
import Footer from './components/Footer';
import GeminiChatDrawer from './components/GeminiChatDrawer';
import { ErrorBoundary } from './components/ErrorBoundary';

const SoulProfileManager = React.lazy(() => import('./components/SoulProfileManager'));
const DlasEngine = React.lazy(() => import('./components/DlasEngine'));
const GeminiIntelligenceSuite = React.lazy(() => import('./components/GeminiIntelligenceSuite'));
const WorkStationSuite = React.lazy(() => import('./components/WorkStationSuite'));
const MarketplaceExchange = React.lazy(() => import('./components/MarketplaceExchange'));
const EducationSystemHub = React.lazy(() => import('./components/EducationSystemHub'));
const PreservedWorkShowcase = React.lazy(() => import('./components/PreservedWorkShowcase'));
const MeUusStudio = React.lazy(() => import('./components/MeUusStudio'));
const SelfBuildingEcosystem = React.lazy(() => import('./components/SelfBuildingEcosystem'));
const CareerSkillGraph = React.lazy(() => import('./components/CareerSkillGraph'));
const TransparencyDashboard = React.lazy(() => import('./components/TransparencyDashboard'));

// Standalone Routed Pages
const EvidencePage = React.lazy(() => import('./components/EvidencePage'));
const SafetyPage = React.lazy(() => import('./components/SafetyPage'));
const AccessibilityPage = React.lazy(() => import('./components/AccessibilityPage'));
const AboutPage = React.lazy(() => import('./components/AboutPage'));
const AppreciationPage = React.lazy(() => import('./components/AppreciationPage'));
const DlasWebEmbed = React.lazy(() => import('./components/DlasWebEmbed'));
const DlasControlEmbed = React.lazy(() => import('./components/DlasControlEmbed'));
const VisualAssetsGallery = React.lazy(() => import('./components/VisualAssetsGallery'));

import { motion, useScroll, useSpring } from 'motion/react';
import { AgentCoreProvider } from './components/AgentCoreProvider';
import { AuthProvider } from './context/AuthContext';
import { EcosystemProvider } from './context/EcosystemContext';

function SectionLoader() {
  return (
    <div className="flex items-center justify-center py-24">
      <div className="animate-pulse flex flex-col items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-emerald-500/20 animate-spin border-2 border-emerald-500 border-t-transparent" />
        <span className="text-slate-500 text-sm">Loading section…</span>
      </div>
    </div>
  );
}

interface ScrollRevealProps {
  children: React.ReactNode;
  id?: string;
  className?: string;
  delay?: number;
}

function ScrollReveal({ children, id, className = '', delay = 0 }: ScrollRevealProps) {
  return (
    <motion.div
      id={id}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function App() {
  const [currentRoute, setCurrentRoute] = useState<string>('home');

  useEffect(() => {
    const handleRouteChange = () => {
      const hash = window.location.hash.replace('#', '').toLowerCase();
      const path = window.location.pathname.replace('/', '').toLowerCase();
      const active = hash || path;
      if (active === 'evidence') {
        setCurrentRoute('evidence');
      } else if (active === 'safety') {
        setCurrentRoute('safety');
      } else if (active === 'accessibility') {
        setCurrentRoute('accessibility');
      } else if (active === 'about') {
        setCurrentRoute('about');
      } else if (active === 'appreciation') {
        setCurrentRoute('appreciation');
      } else if (active === 'dlas-web') {
        setCurrentRoute('dlas-web');
      } else if (active === 'dlas-control') {
        setCurrentRoute('dlas-control');
      } else if (active === 'visual-assets' || active === 'visual') {
        setCurrentRoute('visual-assets');
      } else {
        setCurrentRoute('home');
      }
    };
    handleRouteChange();
    window.addEventListener('hashchange', handleRouteChange);
    window.addEventListener('popstate', handleRouteChange);
    return () => {
      window.removeEventListener('hashchange', handleRouteChange);
      window.removeEventListener('popstate', handleRouteChange);
    };
  }, []);

  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30, restDelta: 0.001 });

  return (
    <AuthProvider>
      <AgentCoreProvider>
        <EcosystemProvider>
          <div className="relative min-h-screen bg-slate-950 text-slate-100 selection:bg-emerald-500 selection:text-white">
            <motion.div className="fixed top-0 left-0 right-0 h-1 bg-emerald-500 origin-left z-[60]" style={{ scaleX }} />
            <Navbar />
            <main>
              {currentRoute === 'evidence' && (
                <ScrollReveal><ErrorBoundary sectionName="Evidence Page"><Suspense fallback={<SectionLoader />}><EvidencePage /></Suspense></ErrorBoundary></ScrollReveal>
              )}
              {currentRoute === 'safety' && (
                <ScrollReveal><ErrorBoundary sectionName="Safety Page"><Suspense fallback={<SectionLoader />}><SafetyPage /></Suspense></ErrorBoundary></ScrollReveal>
              )}
              {currentRoute === 'accessibility' && (
                <ScrollReveal><ErrorBoundary sectionName="Accessibility Page"><Suspense fallback={<SectionLoader />}><AccessibilityPage /></Suspense></ErrorBoundary></ScrollReveal>
              )}
              {currentRoute === 'about' && (
                <ScrollReveal><ErrorBoundary sectionName="About Page"><Suspense fallback={<SectionLoader />}><AboutPage /></Suspense></ErrorBoundary></ScrollReveal>
              )}
              {currentRoute === 'appreciation' && (
                <ScrollReveal><ErrorBoundary sectionName="Appreciation Page"><Suspense fallback={<SectionLoader />}><AppreciationPage /></Suspense></ErrorBoundary></ScrollReveal>
              )}
              {currentRoute === 'dlas-web' && (
                <ScrollReveal><ErrorBoundary sectionName="DLAS Web"><Suspense fallback={<SectionLoader />}><DlasWebEmbed /></Suspense></ErrorBoundary></ScrollReveal>
              )}
              {currentRoute === 'dlas-control' && (
                <ScrollReveal><ErrorBoundary sectionName="DLAS Control"><Suspense fallback={<SectionLoader />}><DlasControlEmbed /></Suspense></ErrorBoundary></ScrollReveal>
              )}
              {currentRoute === 'visual-assets' && (
                <ScrollReveal><ErrorBoundary sectionName="Visual Assets"><Suspense fallback={<SectionLoader />}><VisualAssetsGallery /></Suspense></ErrorBoundary></ScrollReveal>
              )}
              {currentRoute === 'home' && (
                <ErrorBoundary sectionName="meUus Home">
                <Suspense fallback={<SectionLoader />}>
                  <Hero />
                  <ScrollReveal id="meyoo"><UniversalMeYooOS /></ScrollReveal>
                  <ScrollReveal id="video-experience"><VideoExperienceHub /></ScrollReveal>
                  <ScrollReveal id="full-ecosystem"><FullEcosystemHub /></ScrollReveal>
                  <ScrollReveal id="workspace" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"><WorkspaceCloudSqlHub /></ScrollReveal>
                  <ScrollReveal id="human-journey"><HumanJourneyKernel /></ScrollReveal>
                  <ScrollReveal><FourDomainNavigator /></ScrollReveal>
                  <ScrollReveal id="start"><UniversalStartDoor /></ScrollReveal>
                  <ScrollReveal id="app-preview"><InteractiveAppShowcase /></ScrollReveal>
                  <ScrollReveal id="dashboard"><StartingPointDashboard /></ScrollReveal>
                  <ScrollReveal id="soul-journeys"><QuranSeerahJourneys /></ScrollReveal>
                  <ScrollReveal id="soul"><SoulProfileManager /></ScrollReveal>
                  <ScrollReveal><DlasEngine /></ScrollReveal>
                  <ScrollReveal><GeminiIntelligenceSuite /></ScrollReveal>
                  <ScrollReveal id="workstation"><WorkStationSuite /></ScrollReveal>
                  <ScrollReveal id="studio"><MeUusStudio /></ScrollReveal>
                  <ScrollReveal id="self-building"><SelfBuildingEcosystem /></ScrollReveal>
                  <ScrollReveal id="career-skill-graph"><CareerSkillGraph /></ScrollReveal>
                  <ScrollReveal id="photo-album"><PhotoAlbumGallery /></ScrollReveal>
                  <ScrollReveal><KnowledgeHub /></ScrollReveal>
                  <ScrollReveal id="education"><EducationSystemHub /></ScrollReveal>
                  <ScrollReveal id="marketplace"><MarketplaceExchange /></ScrollReveal>
                  <ScrollReveal id="pillars"><PillarsGrid /></ScrollReveal>
                  <ScrollReveal id="wellness"><WellnessCheck /></ScrollReveal>
                  <ScrollReveal><IdeaLabGate /></ScrollReveal>
                  <ScrollReveal><GoogleClassroomHub /></ScrollReveal>
                  <ScrollReveal><ProjectTechOverview /></ScrollReveal>
                  <ScrollReveal id="founder"><FounderStory /></ScrollReveal>
                  <ScrollReveal id="preservation"><PreservedWorkShowcase /></ScrollReveal>
                  <ScrollReveal><TransparencyDashboard /></ScrollReveal>
                  <ScrollReveal><Roadmap /></ScrollReveal>
                  <ScrollReveal>
                    <section className="py-24 px-6 bg-gradient-to-b from-slate-900 to-emerald-950 text-white text-center border-t border-slate-800">
                      <div className="max-w-3xl mx-auto space-y-8">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold uppercase tracking-wider">The meUus Covenant</div>
                        <h2 className="text-4xl md:text-6xl font-serif font-bold tracking-tight text-white leading-tight">No citizen of Bangladesh should ever be left behind.</h2>
                        <p className="text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">Whether you are seeking work, resolving a legal question, formalizing a business, seeking emotional clarity, or offering your skills to another—meUus walks beside you.</p>
                        <div className="flex flex-wrap justify-center gap-4 pt-4">
                          <a href="#start" className="px-8 py-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full font-bold text-base md:text-lg transition-all shadow-xl shadow-emerald-900/40">Enter Universal Start Door</a>
                          <a href="#soul" className="px-8 py-4 bg-slate-900 hover:bg-slate-800 text-slate-200 hover:text-white border border-slate-700 rounded-full font-bold text-base md:text-lg transition-all">Build Master Soul Profile</a>
                        </div>
                      </div>
                    </section>
                  </ScrollReveal>
                </Suspense>
                </ErrorBoundary>
              )}
            </main>
            <Footer />
            <GeminiChatDrawer />
          </div>
        </EcosystemProvider>
      </AgentCoreProvider>
    </AuthProvider>
  );
}